// api/users.js
import { supabase } from '../supabaseClient';
import { debug } from '../helpers/debug';
import { sanitizeInputByType } from '../helpers/formatHelpers';

/**
 * Syncs the Supabase auth user to the local users table.
 * If the user does not exist, it will create a new record.
 * Returns the combined user object with projects and role.
 */
export async function syncAuthToUser(authUser) {
  if (!authUser?.id) {
    throw new Error('Invalid auth user');
  }

  // Check if the user exists in the users table
  const existingUser = await getUserById(authUser.id);

    // Insert if missing
    if (!existingUser) {
      await createUser({
        id: authUser.id,
        email: authUser.email,
        name: authUser.user_metadata?.full_name || authUser.email,
      });

      // Create default project for new user using centralized API
      try {
        const { apiCreateProject } = await import('./projects');
        await apiCreateProject(authUser.id, 'My Project');
      } catch (e) {
        // Fallback: direct supabase insert if import fails
        await supabase
          .from('projects')
          .insert([{ name: 'My Project', owner_user_id: authUser.id }]);
      }
    } else {
    // Update last_sign_in timestamp
    await supabase
      .from('users')
      .update({ last_sign_in: new Date().toISOString() })
      .eq('id', authUser.id);
  }

  // Fetch user data with role
  const { data: userData, error: userError } = await supabase
    .from('users')
    .select('role')
    .eq('id', authUser.id)
    .single();

  if (userError) {
    debug('[syncAuthToUser] fetch user role error:', { type: 'error' }, userError);
  }

  // Fetch projects owned by this user
  const { data: projects, error: projectsError } = await supabase
    .from('projects')
    .select('*')
    .eq('owner_user_id', authUser.id);

  if (projectsError) {
    debug('[syncAuthToUser] fetch projects error:', { type: 'error' }, projectsError);
    throw projectsError;
  }

  return { 
    ...authUser, 
    role: userData?.role || null,
    projects: projects || [] 
  };
}

/**
 * API endpoint helper to get the current authenticated user
 */
export async function getCurrentUser(req, res) {
  // 1. Get user from Supabase auth
  const { data: { user: authUser } } = await supabase.auth.getUser();

  if (!authUser) return res.status(401).json({ error: 'Not authenticated' });

  // 2. Try to get the user from your users table
  let user = await getUserById(authUser.id);

  // 3. If missing, create a new record
  if (!user) {
    user = await createUser({
      id: authUser.id,
      email: authUser.email,
      name: authUser.user_metadata?.full_name || null,
    });
  }

  // 4. Return combined user object
  return res.status(200).json({ user });
}

/**
 * Get a user by ID from the local users table
 */
export async function getUserById(id) {
  // Try once, and retry a single time if PostgREST returns 406 (sometimes observed on fresh signups)
  let attempt = 0;
  while (attempt < 2) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .maybeSingle(); // Use maybeSingle() instead of single() to handle 0 rows gracefully

    if (!error) return data; // Returns null if no rows, or the user object if found

    // If 406 Not Acceptable, wait briefly and retry once
    if (error.status === 406 && attempt === 0) {
      debug('[getUserById] Received 406 from PostgREST, retrying shortly', { type: 'warn', level: 1 }, { id });
      await new Promise(r => setTimeout(r, 200));
      attempt++;
      continue;
    }

    debug('[getUserById] supabase error', { type: 'error' }, error);
    return null;
  }
  return null;
}

/**
 * Create a new user record in the local users table
 */
export async function createUser({ id, email, name }) {
  const safeId = sanitizeInputByType(id, 'identifier', { maxLength: 120 });
  const safeEmail = sanitizeInputByType(email, 'text', { maxLength: 320 }).trim().toLowerCase() || null;
  const safeName = sanitizeInputByType(name, 'title', { maxLength: 180 }) || null;

  const { data, error } = await supabase
    .from('users')
    .insert([{ id: safeId, email: safeEmail, name: safeName }])
    .select()
    .single();

  if (error) throw error;
  return data;
}
